import { useState, useEffect, useCallback } from 'react'
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  getDocs
} from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import CircularProgress from '@mui/material/CircularProgress'
import Chip from '@mui/material/Chip'
import Link from '@mui/material/Link'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import { useAdminStore } from '../../../stores/adminStore'
import { useTestDataStore } from '../../../stores/testDataStore'
import Database from '../../../types/Database'

interface PendingUser {
  id: string
  displayName: string
  accreditation: Database.AccreditationObject
}

const AccreditationReviewTab = () => {
  const validateAdminRole = useAdminStore(s => s.validateAdminRole)
  const showTestData = useTestDataStore(s => s.showTestData)

  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([])
  const [selectedUser, setSelectedUser] = useState<PendingUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [showRejectInput, setShowRejectInput] = useState(false)
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({ open: false, message: '', severity: 'success' })

  const fetchPendingAccreditations = useCallback(async () => {
    setLoading(true)
    try {
      const db = getFirestore()
      const usersRef = collection(db, 'users')
      const q = query(
        usersRef,
        where('accreditation.status', '==', 'pending'),
        where('testData', '==', showTestData),
        orderBy('accreditation.submittedAt', 'asc')
      )
      const snapshot = await getDocs(q)
      const users: PendingUser[] = snapshot.docs.map(doc => ({
        id: doc.id,
        displayName: doc.data().displayName ?? 'Unknown',
        accreditation: doc.data().accreditation as Database.AccreditationObject
      }))
      setPendingUsers(users)
    } catch {
      setSnackbar({
        open: true,
        message: 'Failed to load pending accreditations',
        severity: 'error'
      })
    } finally {
      setLoading(false)
    }
  }, [showTestData])

  useEffect(() => {
    fetchPendingAccreditations()
  }, [fetchPendingAccreditations, showTestData])

  const handleApprove = async () => {
    if (!selectedUser) return

    const isAdmin = await validateAdminRole()
    if (!isAdmin) {
      setSnackbar({
        open: true,
        message: 'Insufficient permissions',
        severity: 'error'
      })
      return
    }

    setActionLoading(true)
    try {
      const functions = getFunctions()
      const approve = httpsCallable<{ userId: string }, { success: boolean }>(
        functions,
        'approveAccreditation'
      )
      await approve({ userId: selectedUser.id })
      setSnackbar({
        open: true,
        message: 'Accreditation approved successfully',
        severity: 'success'
      })
      setSelectedUser(null)
      await fetchPendingAccreditations()
    } catch (error: any) {
      if (error?.code === 'functions/failed-precondition') {
        setConflictDialogOpen(true)
      } else {
        setSnackbar({
          open: true,
          message: error?.message ?? 'Failed to approve accreditation',
          severity: 'error'
        })
      }
    } finally {
      setActionLoading(false)
    }
  }

  const handleReject = async () => {
    if (!selectedUser) return
    if (rejectReason.length < 1 || rejectReason.length > 500) return

    const isAdmin = await validateAdminRole()
    if (!isAdmin) {
      setSnackbar({
        open: true,
        message: 'Insufficient permissions',
        severity: 'error'
      })
      return
    }

    setActionLoading(true)
    try {
      const functions = getFunctions()
      const reject = httpsCallable<
        { userId: string; reason: string },
        { success: boolean }
      >(functions, 'rejectAccreditation')
      await reject({ userId: selectedUser.id, reason: rejectReason })
      setSnackbar({
        open: true,
        message: 'Accreditation rejected',
        severity: 'success'
      })
      setSelectedUser(null)
      setShowRejectInput(false)
      setRejectReason('')
      await fetchPendingAccreditations()
    } catch (error: any) {
      if (error?.code === 'functions/failed-precondition') {
        setConflictDialogOpen(true)
      } else {
        setSnackbar({
          open: true,
          message: error?.message ?? 'Failed to reject accreditation',
          severity: 'error'
        })
      }
    } finally {
      setActionLoading(false)
    }
  }

  const handleConflictClose = async () => {
    setConflictDialogOpen(false)
    setSelectedUser(null)
    setShowRejectInput(false)
    setRejectReason('')
    await fetchPendingAccreditations()
  }

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return 'N/A'
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  const getDocumentType = (url: string): 'image' | 'pdf' => {
    const lower = url.toLowerCase()
    if (lower.includes('.pdf')) return 'pdf'
    return 'image'
  }

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' py={4}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant='h6' gutterBottom>
        Accreditation Review
      </Typography>

      {pendingUsers.length === 0 && (
        <Alert severity='info'>No pending accreditation submissions.</Alert>
      )}

      <Box display='flex' gap={3}>
        {/* List panel */}
        {pendingUsers.length > 0 && (
          <Box sx={{ width: 360, flexShrink: 0 }}>
            <List
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                maxHeight: 600,
                overflow: 'auto'
              }}
            >
              {pendingUsers.map(user => (
                <ListItemButton
                  key={user.id}
                  selected={selectedUser?.id === user.id}
                  onClick={() => {
                    setSelectedUser(user)
                    setShowRejectInput(false)
                    setRejectReason('')
                  }}
                >
                  <ListItemText
                    primary={user.displayName}
                    secondary={
                      <>
                        <Chip
                          label={user.accreditation.type.toUpperCase()}
                          size='small'
                          component='span'
                          sx={{ mr: 1 }}
                        />
                        {user.accreditation.level && (
                          <Chip
                            label={user.accreditation.level}
                            size='small'
                            component='span'
                            variant='outlined'
                            sx={{ mr: 1 }}
                          />
                        )}
                        {formatDate(user.accreditation.submittedAt)}
                      </>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>
        )}

        {/* Detail panel */}
        {selectedUser && (
          <Box sx={{ flex: 1 }}>
            <Card variant='outlined'>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  Submission Details
                </Typography>

                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant='caption' color='text.secondary'>
                      Full Name
                    </Typography>
                    <Typography>
                      {selectedUser.accreditation.fullName}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant='caption' color='text.secondary'>
                      Date of Birth
                    </Typography>
                    <Typography>
                      {formatDate(selectedUser.accreditation.dateOfBirth)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant='caption' color='text.secondary'>
                      Accreditation Type
                    </Typography>
                    <Typography>
                      {selectedUser.accreditation.type.toUpperCase()}
                    </Typography>
                  </Box>

                  {selectedUser.accreditation.level && (
                    <Box>
                      <Typography variant='caption' color='text.secondary'>
                        Level
                      </Typography>
                      <Typography>
                        {selectedUser.accreditation.level}
                      </Typography>
                    </Box>
                  )}

                  <Box>
                    <Typography variant='caption' color='text.secondary'>
                      Certification Number
                    </Typography>
                    <Typography>
                      {selectedUser.accreditation.certificationNumber}
                    </Typography>
                  </Box>

                  {selectedUser.accreditation.optionalMemberId && (
                    <Box>
                      <Typography variant='caption' color='text.secondary'>
                        Member ID
                      </Typography>
                      <Typography>
                        {selectedUser.accreditation.optionalMemberId}
                      </Typography>
                    </Box>
                  )}

                  {selectedUser.accreditation.type === 'padi' && (
                    <Box>
                      <Typography variant='caption' color='text.secondary'>
                        Registered Email (PADI)
                      </Typography>
                      <Typography>
                        {
                          (
                            selectedUser.accreditation as Database.PadiAccreditation
                          ).registeredEmail
                        }
                      </Typography>
                    </Box>
                  )}

                  <Box>
                    <Typography variant='caption' color='text.secondary'>
                      Submitted At
                    </Typography>
                    <Typography>
                      {formatDate(selectedUser.accreditation.submittedAt)}
                    </Typography>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      gutterBottom
                    >
                      Certification Document
                    </Typography>
                    {getDocumentType(selectedUser.accreditation.documentUrl) ===
                    'image' ? (
                      <Box sx={{ mt: 1 }}>
                        <img
                          src={selectedUser.accreditation.documentUrl}
                          alt='Certification document'
                          style={{
                            maxWidth: '100%',
                            maxHeight: 400,
                            borderRadius: 4,
                            border: '1px solid #ddd'
                          }}
                        />
                      </Box>
                    ) : (
                      <Box sx={{ mt: 1 }}>
                        <Link
                          href={selectedUser.accreditation.documentUrl}
                          target='_blank'
                          rel='noopener noreferrer'
                        >
                          View PDF Document
                        </Link>
                      </Box>
                    )}
                  </Box>

                  <Divider />

                  {/* Actions */}
                  <Stack direction='row' spacing={2} sx={{ pt: 1 }}>
                    <Button
                      variant='contained'
                      color='success'
                      onClick={handleApprove}
                      disabled={actionLoading}
                    >
                      {actionLoading ? 'Processing…' : 'Approve'}
                    </Button>
                    <Button
                      variant='outlined'
                      color='error'
                      onClick={() => setShowRejectInput(true)}
                      disabled={actionLoading || showRejectInput}
                    >
                      Reject
                    </Button>
                  </Stack>

                  {showRejectInput && (
                    <Box sx={{ pt: 1 }}>
                      <TextField
                        label='Rejection Reason'
                        multiline
                        rows={3}
                        fullWidth
                        value={rejectReason}
                        onChange={e => setRejectReason(e.target.value)}
                        error={
                          rejectReason.length > 500 ||
                          (showRejectInput && rejectReason.length === 0)
                        }
                        helperText={`${rejectReason.length}/500 characters${
                          rejectReason.length === 0
                            ? ' (required)'
                            : rejectReason.length > 500
                            ? ' (too long)'
                            : ''
                        }`}
                        disabled={actionLoading}
                      />
                      <Stack direction='row' spacing={1} sx={{ mt: 1 }}>
                        <Button
                          variant='contained'
                          color='error'
                          onClick={handleReject}
                          disabled={
                            actionLoading ||
                            rejectReason.length < 1 ||
                            rejectReason.length > 500
                          }
                        >
                          {actionLoading ? 'Processing…' : 'Confirm Rejection'}
                        </Button>
                        <Button
                          variant='text'
                          onClick={() => {
                            setShowRejectInput(false)
                            setRejectReason('')
                          }}
                          disabled={actionLoading}
                        >
                          Cancel
                        </Button>
                      </Stack>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>

      {/* Conflict dialog */}
      <Dialog open={conflictDialogOpen} onClose={handleConflictClose}>
        <DialogTitle>Already Reviewed</DialogTitle>
        <DialogContent>
          <Typography>
            This accreditation submission has already been reviewed by another
            admin. The list will be refreshed to show the current state.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConflictClose} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default AccreditationReviewTab
